/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */
import { useMutation } from 'react-query'

import type { QueryFunctionReturnType } from '@modules/query/types'
import { invalidateGetBudget } from '@modules/billing-lib/queries/useGetBudget'
import { invalidateGetBudgets } from '@modules/billing-lib/queries/useGetBudgets'
import { updateBudgetV1 } from '@modules/billing-api/v1/callers'
import type { operations } from '@modules/billing-api/v1/types'

import type { MutateOptions } from 'react-query'

export type UpdateBudgetBody =
  operations['updateBudgetV1']['requestBody']['content']['application/json']

type UpdateBudgetVariables = {
  budgetId: operations['updateBudgetV1']['parameters']['path']['budget_id']
  organizationId: operations['updateBudgetV1']['parameters']['path']['organization_id']
  body: UpdateBudgetBody
}

const useUpdateBudget = () => {
  const mutation = useMutation<
    QueryFunctionReturnType<typeof updateBudgetV1>,
    unknown,
    UpdateBudgetVariables
  >({
    mutationFn: ({ budgetId, organizationId, body }) =>
      updateBudgetV1({
        pathParameters: { budget_id: budgetId, organization_id: organizationId },
        body,
      }),
  })

  /* 
    We need this implementation because invalidateGetBudgets unmounts the components  
    that consume this hook when it’s invoked, affecting the expected behavior
  */
  const mutate = (
    vars: UpdateBudgetVariables,
    opts: MutateOptions<void, unknown, UpdateBudgetVariables, unknown> | undefined,
  ) => {
    mutation.mutate(vars, {
      ...opts,
      onSuccess: (...args) => {
        invalidateGetBudget(vars.organizationId, vars.budgetId)
        invalidateGetBudgets(vars.organizationId)
        opts?.onSuccess?.(...args)
      },
    })
  }

  return { ...mutation, mutate }
}

export default useUpdateBudget
